import { useCallback, useMemo } from 'react';
import { EMPTY_STRING, FOCUS_TEMPLATE_FEATURE, TAB } from 'constants/general';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import {
  changePackCreatorName,
  changePackFeaturedForOnboardingStatus,
  changePackIsFeaturedStatus,
  changePackMarketplaceApprovalStatus
} from 'store/reducer/setting/slice';
import { updateFilterIsAdminToolbarShow } from 'store/reducer/filter/slice';
import AdminFilled from 'assets/icons/AdminFilled';
import COLOR from 'constants/color';
import CloseCircle from 'assets/icons/CloseCircle';
import { updateCurrentViewedFocusTemplate } from 'store/reducer/focus-mode/slice';
import { t } from 'services/i18n.service';
import Switch from 'components/ui/Switch';
import { BaseButtonXs } from 'components/ui/BaseButton';
import { adminWidgetToolbarSelector } from 'store/reducer/setting/selectors';
import FloatingLabelInput from 'components/ui/FloatingLabelInput';

const switchLabelClassName =
  '~text-xs/base line-clamp-1 text-primary-900 dark:text-secondary-dark-900 font-medium';

export default function AdminWidgets() {
  const dispatch = useAppDispatch();
  const isAdminToolbarShown = useAppSelectorShallow(
    (state) => state.filter.isAdminToolbarShown
  );
  const { tabs, activeTabIndex, currentSettings, currentViewedFocusTemplate } =
    useAppSelectorShallow(adminWidgetToolbarSelector);

  const isPackInfo = useMemo(
    () => tabs[activeTabIndex] === TAB.PACK_INFO,
    [tabs, activeTabIndex]
  );

  const source = isPackInfo ? currentSettings : currentViewedFocusTemplate;

  const [authorInputLabel, authorName] = useMemo(() => {
    const label = t(
      isPackInfo ? 'packs.creator_name' : 'focus_mode.author_name'
    );
    const name = isPackInfo
      ? currentSettings?.creator_name
      : currentViewedFocusTemplate?.author_name;
    return [label, name];
  }, [
    isPackInfo,
    currentSettings?.creator_name,
    currentViewedFocusTemplate?.author_name
  ]);

  const [isFeatured, featuredForOnboarding, marketplaceApprovalStatus] =
    useMemo(
      () => [
        source?.is_featured,
        source?.featured_for_onboarding,
        source?.marketplace_approval_status
      ],
      [source]
    );

  const handleChange = useCallback(
    (feature: string, value: string | boolean) => {
      if (isPackInfo) {
        switch (feature) {
          case FOCUS_TEMPLATE_FEATURE.AUTHOR_NAME:
            dispatch(changePackCreatorName(value as string));
            break;
          case FOCUS_TEMPLATE_FEATURE.IS_FEATURED:
            dispatch(changePackIsFeaturedStatus(value as boolean));
            break;
          case FOCUS_TEMPLATE_FEATURE.MARKETPLACE_APPROVAL_STATUS:
            dispatch(changePackMarketplaceApprovalStatus(value as boolean));
            break;
          default:
            dispatch(changePackFeaturedForOnboardingStatus(value as boolean));
        }
      } else {
        dispatch(
          updateCurrentViewedFocusTemplate({
            focusTemplateFeature: feature,
            value
          })
        );
      }
    },
    [isPackInfo, dispatch]
  );

  const adminToggles = useMemo(
    () => [
      {
        feature: FOCUS_TEMPLATE_FEATURE.IS_FEATURED,
        label: t('market.is_featured'),
        checked: isFeatured ?? false
      },
      {
        feature: FOCUS_TEMPLATE_FEATURE.MARKETPLACE_APPROVAL_STATUS,
        label: t('market.marketplace_approval_status'),
        checked: marketplaceApprovalStatus ?? false
      },
      {
        feature: FOCUS_TEMPLATE_FEATURE.FEATURE_FOR_ONBOARDING,
        label: t('market.featured_for_onboarding'),
        checked: featuredForOnboarding ?? false
      }
    ],
    [isFeatured, marketplaceApprovalStatus, featuredForOnboarding]
  );

  return (
    <div className='w-fit h-fit flex justify-end gap-1 absolute top-0 right-4'>
      {isAdminToolbarShown && (
        <div
          className='w-full h-fit flex flex-wrap items-center ~gap-3/6 rounded animate-slideIn border px-3 py-2 bg-primary-50 border-primary-200 text-primary-900 dark:bg-primary-dark-100 dark:border-primary-dark-400 dark:text-secondary-dark-900 z-20 [&_h6]:text-primary-800 dark:[&_h6]:text-secondary-dark-900'
          data-testid='admin-toolbar-wrapper'>
          <FloatingLabelInput
            label={authorInputLabel}
            onChange={({ target: { value } }) =>
              handleChange(FOCUS_TEMPLATE_FEATURE.AUTHOR_NAME, value)
            }
            value={authorName ?? EMPTY_STRING}
            type='author_name'
            placeholder={authorInputLabel}
            size='small'
            error={!authorName?.trim() ? ' ' : EMPTY_STRING}
          />
          {adminToggles.map(({ feature, label, checked }) => (
            <Switch
              key={feature}
              checked={checked}
              onChange={(next) => handleChange(feature, next)}
              label={label}
              labelClassName={switchLabelClassName}
              className='flex-shrink-0 safari-flex-fix'
            />
          ))}
        </div>
      )}
      <BaseButtonXs
        variant='tertiary'
        onClick={() => {
          dispatch(updateFilterIsAdminToolbarShow(!isAdminToolbarShown));
        }}
        data-testid='admin-toolbar-toggle-btn'
        icon={
          isAdminToolbarShown ? (
            <CloseCircle fill={COLOR.PRIMARY.LIGHT_900} />
          ) : (
            <AdminFilled />
          )
        }
        additionalStyles='w-fit h-fit'
      />
    </div>
  );
}
